import axios from 'axios'
import { PersonConnection, PersonDetails, OwnershipGraphData, CompanyFinancials, PersonFinancialFootprint } from '../types'

// Same-origin /api: Vite dev server proxies to PHP (see vite.config.ts and BACKEND_URL).
const API_BASE_URL = '/api'

// Types based on CVR API documentation
export interface Company {
  cvrNummer: string
  navn: string
  adresse?: string
  postdistrikt?: string
  postnummer?: string
  virksomhedsform?: string
  startdato?: string
  telefon?: string
  email?: string
  branche?: string
  ansatte?: number
}

export interface SearchResult {
  companies: Company[]
  totalCount: number
  page: number
  pageSize: number
}

export interface PersonSummary {
  enhedsNummer: number | string | null
  navn: string
  adresse?: string
  isAddressConfidential: boolean
}

export interface PersonSearchResult {
  persons: PersonSummary[]
  totalCount: number
  page: number
  pageSize: number
}

export interface CompanyDetails extends Company {
  // Extended company details
  kreditoplysninger?: any
  reklamebeskyttelse?: any
  deltagere?: any[]
  produktionEnheder?: any[]
  ownership?: any
  /** Current legal subsidiaries (EJERREGISTER, primary list). */
  subsidiaryCount?: number
  /** CVR STIFTER rows (same shape as ownership list entries) */
  founders?: any[]
  /** Ledelse i moderselskab på stiftelsesdato (se `FoundingParentManagementSnapshot` i types) */
  foundingManagementAtParents?: any[]
  deltagerRelation?: any[]
}

// Create axios instance with default config.
// `withCredentials` is required so the browser includes our PHP session
// cookie on every API call — see backend/Auth.php for the cookie config.
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

export { apiClient }

/** Graph traversal can chain many CVR lookups — longer than default API calls. */
const GRAPH_REQUEST_TIMEOUT_MS = 60_000
/** Company profile chains founder/parent/subsidiary lookups — allow more time. */
const COMPANY_REQUEST_TIMEOUT_MS = 45_000
/** Person profile can return large affiliation payloads from CVR. */
const PERSON_REQUEST_TIMEOUT_MS = 45_000
const FINANCIAL_REQUEST_TIMEOUT_MS = 60_000
const COMPANY_REQUEST_MAX_RETRIES = 1
const PERSON_REQUEST_MAX_RETRIES = 1
/** Depth for each expand-up / expand-down request (levels from the anchor). */
export const GRAPH_EXPAND_DEPTH = 3

function isAxiosTimeout(error: unknown): boolean {
  return axios.isAxiosError(error) && error.code === 'ECONNABORTED'
}

// Request interceptor for error handling
apiClient.interceptors.request.use(
  (config) => {
    // Add any auth headers here if needed
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor: surface a session-expired event so the React tree
// can redirect to /login. The 401s coming from /auth/* are expected during
// the initial session check and are NOT broadcast — fetchCurrentUser
// handles them locally.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status
    const url: string = error.config?.url || ''
    if (status === 401 && !url.startsWith('/auth/')) {
      window.dispatchEvent(new CustomEvent('xosa-origin:session-expired'))
    }
    return Promise.reject(error)
  }
)

/**
 * Search for companies by query
 * @param query - Search query (company name, CVR number, or address)
 * @param page - Page number (default: 1)
 * @param pageSize - Results per page (default: 20)
 */
export const searchCompanies = async (
  query: string,
  page: number = 1,
  pageSize: number = 20,
  includeInactive: boolean = false
): Promise<SearchResult> => {
  try {
    const response = await apiClient.get('/search', {
      params: {
        q: query,
        page,
        pageSize,
        ...(includeInactive ? { includeInactive: '1' } : {}),
      },
    })

    const payload = response.data.data
    return {
      companies: payload?.companies ?? [],
      totalCount: typeof payload?.totalCount === 'number' ? payload.totalCount : 0,
      page: typeof payload?.page === 'number' ? payload.page : page,
      pageSize: typeof payload?.pageSize === 'number' ? payload.pageSize : pageSize,
    }
  } catch (error) {
    console.error('Search companies error:', error)
    throw new Error('Failed to search companies')
  }
}

/**
 * Get detailed company information by CVR number
 * @param cvrNumber - Company CVR number
 */
export const getCompanyDetails = async (cvrNumber: string): Promise<CompanyDetails> => {
  let lastError: unknown

  for (let attempt = 0; attempt <= COMPANY_REQUEST_MAX_RETRIES; attempt++) {
    try {
      const response = await apiClient.get(`/company/${cvrNumber}`, {
        timeout: COMPANY_REQUEST_TIMEOUT_MS,
      })
      return response.data.data
    } catch (error) {
      lastError = error
      if (attempt < COMPANY_REQUEST_MAX_RETRIES && isAxiosTimeout(error)) {
        continue
      }
      break
    }
  }

  console.error('Get company details error:', lastError)
  if (isAxiosTimeout(lastError)) {
    throw new Error('Hentning af virksomhed tog for lang tid — prøv igen')
  }
  throw new Error('Kunne ikke hente virksomheden')
}

/**
 * Latest annual-report headline figures (omsætning + resultat) with YoY change.
 */
export const getCompanyFinancials = async (cvrNumber: string): Promise<CompanyFinancials> => {
  try {
    const response = await apiClient.get(`/company/${cvrNumber}/financials`, {
      timeout: FINANCIAL_REQUEST_TIMEOUT_MS,
    })
    return response.data.data
  } catch (error) {
    console.error('Get company financials error:', error)
    throw new Error('Kunne ikke hente regnskabstal')
  }
}

/**
 * Search for persons by name
 */
export const searchPersons = async (
  query: string,
  page: number = 1,
  pageSize: number = 20
): Promise<PersonSearchResult> => {
  try {
    const response = await apiClient.get('/search/persons', {
      params: { q: query, page, pageSize },
    })
    const payload = response.data.data
    return {
      persons: payload?.persons ?? [],
      totalCount: typeof payload?.totalCount === 'number' ? payload.totalCount : 0,
      page: typeof payload?.page === 'number' ? payload.page : page,
      pageSize: typeof payload?.pageSize === 'number' ? payload.pageSize : pageSize,
    }
  } catch (error) {
    console.error('Search persons error:', error)
    throw new Error('Failed to search persons')
  }
}

/**
 * Search companies by address
 * @param address - Address to search for
 * @param page - Page number (default: 1)
 * @param pageSize - Results per page (default: 20)
 */
export const searchCompaniesByAddress = async (
  address: string,
  page: number = 1,
  pageSize: number = 20
): Promise<Company[]> => {
  try {
    const response = await apiClient.get('/search/address', {
      params: {
        address,
        page,
        pageSize,
      },
    })

    return response.data.companies || []
  } catch (error) {
    console.error('Search by address error:', error)
    throw new Error('Failed to search companies by address')
  }
}

/**
 * Get company production units
 * @param cvrNumber - Company CVR number
 */
export const getCompanyProductionUnits = async (cvrNumber: string): Promise<any[]> => {
  try {
    const response = await apiClient.get(`/company/${cvrNumber}/production-units`)
    return response.data.productionUnits || []
  } catch (error) {
    console.error('Get production units error:', error)
    throw new Error('Failed to get production units')
  }
}

/**
 * Get company participants/representatives
 * @param cvrNumber - Company CVR number
 */
export const getCompanyParticipants = async (cvrNumber: string): Promise<any[]> => {
  try {
    const response = await apiClient.get(`/company/${cvrNumber}/participants`)
    return response.data.participants || []
  } catch (error) {
    console.error('Get participants error:', error)
    throw new Error('Failed to get company participants')
  }
}

/**
 * Get person details by enhedsNummer
 * @param enhedsNummer - Person entity number
 */
export const getPersonDetails = async (enhedsNummer: string): Promise<PersonDetails> => {
  let lastError: unknown

  for (let attempt = 0; attempt <= PERSON_REQUEST_MAX_RETRIES; attempt++) {
    try {
      const response = await apiClient.get(`/person/${enhedsNummer}`, {
        timeout: PERSON_REQUEST_TIMEOUT_MS,
      })
      return response.data.data
    } catch (error) {
      lastError = error
      if (attempt < PERSON_REQUEST_MAX_RETRIES && isAxiosTimeout(error)) {
        continue
      }
      break
    }
  }

  console.error('Get person details error:', lastError)
  if (isAxiosTimeout(lastError)) {
    throw new Error('Hentning af person tog for lang tid — prøv igen')
  }
  throw new Error('Kunne ikke hente personoplysninger')
}

/**
 * Get business connections for a person (co-participants in shared active companies).
 * Connections are fetched lazily — only call when the Forbindelser tab is visible.
 */
export const getPersonConnections = async (enhedsNummer: string): Promise<PersonConnection[]> => {
  try {
    const response = await apiClient.get(`/person/${enhedsNummer}/connections`, {
      timeout: 60_000,
    })
    return response.data.data ?? []
  } catch (error: any) {
    console.error('Get person connections error:', error)
    throw new Error('Failed to get person connections')
  }
}

/**
 * Aggregated revenue scale for ejerskab vs. indflydelse (all active affiliations).
 */
export const getPersonFinancialFootprint = async (
  enhedsNummer: string,
): Promise<PersonFinancialFootprint> => {
  try {
    const response = await apiClient.get(`/person/${enhedsNummer}/financial-footprint`, {
      timeout: 120_000,
    })
    return response.data.data
  } catch (error) {
    console.error('Get person financial footprint error:', error)
    throw new Error('Kunne ikke beregne økonomisk omfang')
  }
}

/**
 * Get the ownership graph for a CVR number.
 * Returns flat {rootCvr, nodes, edges} — see OwnershipGraphData.
 */
export const getOwnershipGraph = async (cvrNumber: string): Promise<OwnershipGraphData> => {
  try {
    const response = await apiClient.get(`/company/${cvrNumber}/graph`, {
      timeout: GRAPH_REQUEST_TIMEOUT_MS,
    })
    return response.data.data
  } catch (error) {
    console.error('Get ownership graph error:', error)
    throw new Error('Failed to get ownership graph')
  }
}

/** Downward expansion fragment — merge client-side with the upward graph. */
export const getOwnershipGraphDownstream = async (
  cvrNumber: string,
  nodesUsed: number,
  fromCvr?: string,
): Promise<OwnershipGraphData> => {
  try {
    const response = await apiClient.get(`/company/${cvrNumber}/graph`, {
      params: {
        expand: 'down',
        nodesUsed,
        depth: GRAPH_EXPAND_DEPTH,
        ...(fromCvr ? { fromCvr } : {}),
      },
      timeout: GRAPH_REQUEST_TIMEOUT_MS,
    })
    return response.data.data
  } catch (error) {
    console.error('Get ownership graph downstream error:', error)
    throw new Error('Failed to get downstream ownership graph')
  }
}

/** Upward expansion from a truncated company — merge client-side. */
export const getOwnershipGraphUpstream = async (
  profileCvr: string,
  fromCvr: string,
  nodesUsed: number,
): Promise<OwnershipGraphData> => {
  try {
    const response = await apiClient.get(`/company/${profileCvr}/graph`, {
      params: { expand: 'up', fromCvr, nodesUsed, depth: GRAPH_EXPAND_DEPTH },
      timeout: GRAPH_REQUEST_TIMEOUT_MS,
    })
    return response.data.data
  } catch (error) {
    console.error('Get ownership graph upstream error:', error)
    throw new Error('Failed to get upstream ownership graph')
  }
}

export default {
  searchCompanies,
  searchPersons,
  getCompanyDetails,
  getOwnershipGraph,
  getOwnershipGraphDownstream,
  getOwnershipGraphUpstream,
  searchCompaniesByAddress,
  getCompanyProductionUnits,
  getCompanyParticipants,
  getPersonDetails,
  getPersonConnections,
}
